import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Laptop, Shield, AlertTriangle, ChevronRight,
  Plus, Activity, TrendingUp, RotateCcw, WifiOff
} from 'lucide-react';
import { Button } from '../components/ui/button';

const endpointCases = [
  {
    id: "case_ep_001",
    type: "noncompliant",
    typeLabel: "Compliance Violation",
    subject: { name: "ACME-LP-0456", user: "Sara Chen", os: "macOS 14.2" },
    status: "pending_approval",
    priority: "p2",
    issue: "Encryption not enabled",
    recommendation: "Propose enforcement bundle",
    agents: ["endpoint"],
    sla: { remaining: "4h", breached: false }
  },
  {
    id: "case_ep_002",
    type: "quarantine",
    typeLabel: "Quarantine Proposal",
    subject: { name: "ACME-LP-0789", user: "Unknown", os: "Windows 11" },
    status: "pending_approval",
    priority: "p1",
    issue: "Malware detected by CrowdStrike",
    recommendation: "Isolate device and wipe",
    agents: ["endpoint", "security"],
    sla: { remaining: "30m", breached: false }
  },
  {
    id: "case_ep_003",
    type: "patch",
    typeLabel: "Patch Rollout",
    subject: { name: "Engineering Fleet", count: 145, os: "macOS" },
    status: "executing",
    priority: "p3",
    issue: "Security patch KB2026-001",
    progress: { completed: 89, total: 145, failed: 2 },
    agents: ["endpoint"],
    sla: { remaining: "24h", breached: false }
  },
  {
    id: "case_ep_004",
    type: "enrollment",
    typeLabel: "Enrollment Drift",
    subject: { name: "12 devices", description: "Unmanaged devices detected on network" },
    status: "pending_approval",
    priority: "p2",
    issue: "Devices connected without MDM enrollment",
    recommendation: "Send enrollment invitation or block",
    agents: ["endpoint"],
    sla: { remaining: "8h", breached: false }
  }
];

import { getLogoByIntegrationId } from '../constants/logos';

const canaryRollouts = [
  {
    id: "canary_001",
    name: "macOS 14.3 Update",
    status: "in_progress",
    canaryGroup: "Engineering Beta",
    canarySize: 15,
    totalTarget: 450,
    successRate: 100,
    rollbackTriggered: false,
    startedAt: "2026-01-14T10:00:00Z"
  },
  {
    id: "canary_002",
    name: "CrowdStrike Agent 7.2",
    status: "paused",
    canaryGroup: "IT Team",
    canarySize: 8,
    totalTarget: 1200,
    successRate: 87.5,
    rollbackTriggered: true,
    rollbackReason: "1 device kernel panic after update",
    startedAt: "2026-01-13T14:00:00Z",
    logoId: "crowdstrike"
  }
];

const endpointMetrics = {
  totalDevices: 1247,
  compliant: 1198,
  noncompliant: 49,
  quarantined: 3,
  complianceRate: 96.1,
  patchRate: 94.2,
  avgEnrollmentTime: "15m"
};

export const Endpoints = () => {
  const navigate = useNavigate();
  const needsApproval = endpointCases.filter(c => c.status === 'pending_approval');

  const handleCreateBundle = () => {
    navigate('/case/case_ep_new/review');
  };

  const getStatusBadge = (status) => {
    const config = {
      executing: 'badge-executing',
      pending_approval: 'badge-pending',
      verifying: 'badge-verifying',
      closed: 'badge-active'
    };
    const labels = {
      executing: 'Executing',
      pending_approval: 'Needs Approval',
      verifying: 'Verifying',
      closed: 'Completed'
    };
    return { className: config[status] || 'badge-neutral', label: labels[status] || status };
  };

  const getPriorityColor = (priority) => {
    const colors = { p1: 'bg-slate-900', p2: 'bg-slate-400', p3: 'bg-primary' };
    return colors[priority] || 'bg-slate-400';
  };

  return (
    <div className="space-y-6" data-testid="endpoints-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Laptop className="w-5 h-5 text-slate-500" />
            Endpoints
          </h1>
          <p className="text-sm text-slate-500 mt-1">Device compliance, patch rollouts, and quarantine management</p>
        </div>
        <Button onClick={handleCreateBundle} size="sm" data-testid="new-action-bundle-btn">
          <Plus className="w-4 h-4 mr-1" />
          Propose Action Bundle
        </Button>
      </div>

      {needsApproval.length > 0 && needsApproval[0].priority === 'p1' && (
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-slate-700 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-slate-900">
                Urgent: {needsApproval[0].typeLabel}
              </h3>
              <p className="text-sm text-slate-700 mt-1">
                {needsApproval[0].subject.name} - {needsApproval[0].issue}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-slate-200 border-slate-400 text-slate-800 hover:bg-slate-300"
                  onClick={() => navigate(`/case/${needsApproval[0].id}/review`)}
                >
                  Review Bundle
                </Button>
                <span className="text-xs text-slate-600">SLA: {needsApproval[0].sla.remaining}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Total Devices</span>
            <Laptop className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{endpointMetrics.totalDevices}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Compliance Rate</span>
            <Shield className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{endpointMetrics.complianceRate}%</div>
          <div className="text-xs text-slate-600 mt-1">{endpointMetrics.noncompliant} noncompliant</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Patch Rate</span>
            <Activity className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{endpointMetrics.patchRate}%</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Quarantined</span>
            <WifiOff className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{endpointMetrics.quarantined}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Endpoint Cases</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {endpointCases.map((caseItem) => {
                const statusBadge = getStatusBadge(caseItem.status);
                return (
                  <div
                    key={caseItem.id}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/case/${caseItem.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-1 h-10 rounded-full ${getPriorityColor(caseItem.priority)}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`badge-status ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                          <span className="text-sm font-medium text-slate-900">
                            {caseItem.typeLabel}: {caseItem.subject.name}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{caseItem.issue}</p>
                        {caseItem.progress && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>{caseItem.progress.completed}/{caseItem.progress.total} devices</span>
                              {caseItem.progress.failed > 0 && (
                                <span className="text-slate-600">{caseItem.progress.failed} failed</span>
                              )}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${(caseItem.progress.completed / caseItem.progress.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {caseItem.recommendation && (
                          <button className="mt-2 text-xs text-primary hover:underline">
                            {caseItem.recommendation}
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">{caseItem.sla.remaining}</div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                Canary Rollouts
              </h2>
            </div>
            <div className="divide-y divide-slate-100">
              {canaryRollouts.map((rollout) => (
                <div key={rollout.id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      {rollout.logoId && getLogoByIntegrationId(rollout.logoId) && (
                        <img src={getLogoByIntegrationId(rollout.logoId).src} alt="" className="w-4 h-4 object-contain" />
                      )}
                      {rollout.name}
                    </span>
                    <span className={`badge-status ${rollout.status === 'in_progress' ? 'badge-executing' :
                      rollout.status === 'paused' ? 'badge-pending' :
                        'badge-active'
                      }`}>
                      {rollout.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    Canary: {rollout.canaryGroup} ({rollout.canarySize} devices)
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={rollout.successRate === 100 ? 'text-primary' : 'text-slate-600'}>
                      {rollout.successRate}% success
                    </span>
                    <span className="text-slate-400">{rollout.totalTarget} target</span>
                  </div>
                  {rollout.rollbackTriggered && (
                    <div className="mt-2 p-2 bg-slate-100 rounded text-xs text-slate-700 flex items-start gap-2">
                      <RotateCcw className="w-3 h-3 mt-0.5" />
                      Rollback: {rollout.rollbackReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Endpoint Agent</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Trust Score</span>
                <span className="font-medium text-primary">97.8%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Rollback Rate</span>
                <span className="font-medium text-slate-900">2.6%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Connectors</span>
                <div className="flex items-center gap-2">
                  {[getLogoByIntegrationId('intune'), getLogoByIntegrationId('jamf')].map((logo, i) => (
                    logo && <img key={i} src={logo.src} alt={logo.alt} className="w-4 h-4 object-contain" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Endpoints;
